import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const WalletPage = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [balance, setBalance] = useState(0);
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBalance, setShowBalance] = useState(true);

  // 🎨 THEME PER COUNTRY (original branding, not copying any real app)
  const theme = isGhana
    ? {
        bgColor: "bg-[#0a0500]",
        cardBg: "bg-[#1a0a00]",
        balanceCardGradient: "from-yellow-500 via-orange-500 to-red-600",
        balanceCardTextColor: "text-white",
        accentColor: "text-yellow-300",
        actionBg: "bg-[#1a0a00] border border-yellow-500/20",
        bottomNavActive: "text-yellow-400",
        currencySymbol: config.currencySymbol,
        walletBrand: "MoMo Wallet",
        walletTagline: "Powered by ProBank Ghana",
        transferToSelf: "To MoMo",
        transferToBank: "To Bank",
        services: [
          { label: "Airtime", emoji: "📱" },
          { label: "Data", emoji: "🌐" },
          { label: "Bills", emoji: "📄" },
          { label: "DSTV", emoji: "📺" },
          { label: "Save", emoji: "💰" },
          { label: "Loans", emoji: "💳" },
          { label: "Invite", emoji: "📨" },
          { label: "More", emoji: "➕" },
        ],
        flag: "🇬🇭",
      }
    : {
        bgColor: "bg-black",
        cardBg: "bg-gray-900",
        balanceCardGradient: "from-emerald-500 to-teal-600",
        balanceCardTextColor: "text-white",
        accentColor: "text-emerald-400",
        actionBg: "bg-gray-900 border border-white/5",
        bottomNavActive: "text-emerald-400",
        currencySymbol: config.currencySymbol,
        walletBrand: "ProBank Wallet",
        walletTagline: "Your money, simplified",
        transferToSelf: "To ProBank",
        transferToBank: "To Bank",
        services: [
          { label: "Airtime", emoji: "📱" },
          { label: "Data", emoji: "🌐" },
          { label: "Betting", emoji: "🎲" },
          { label: "TV", emoji: "📺" },
          { label: "Safebox", emoji: "🔒" },
          { label: "Loan", emoji: "💰" },
          { label: "Invite", emoji: "📨" },
          { label: "More", emoji: "➕" },
        ],
        flag: "🇳🇬",
      };

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const res = await API.get("/dashboard");
        setBalance(res.data?.balances?.account ?? res.data?.user?.balance ?? 0);
        setUser(res.data?.user ?? null);

        const txRes = await API.get("/bank/wallet/history");
        setTransactions(txRes.data?.transactions || []);
      } catch (err) {
        console.error("Error loading wallet data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bgColor} flex items-center justify-center text-white`}>
        <p className="animate-pulse">Loading Wallet...</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bgColor} text-white pb-20`}>

      {/* Top Header */}
      <div className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            Hi, {user?.name || "User"}
            <span className="text-xs">{theme.flag}</span>
          </h1>
          <p className="text-[10px] text-gray-500 mt-0.5">{theme.walletTagline}</p>
        </div>
        <div className="flex gap-3">
          <button className={`w-9 h-9 ${theme.cardBg} rounded-full flex items-center justify-center text-sm`}>🎧</button>
          <button className={`w-9 h-9 ${theme.cardBg} rounded-full flex items-center justify-center text-sm`}>🔔</button>
        </div>
      </div>

      {/* Balance Card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`mx-4 p-5 rounded-2xl bg-gradient-to-br ${theme.balanceCardGradient} shadow-2xl ${theme.balanceCardTextColor}`}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <p className="text-sm opacity-90">Available Balance</p>
            <button onClick={() => setShowBalance(!showBalance)} className="opacity-80 hover:opacity-100">
              👁️
            </button>
          </div>

          <button
            onClick={() => navigate("/opay/history")}
            className="text-sm font-semibold opacity-90 hover:opacity-100"
          >
            History &gt;
          </button>
        </div>

        <p className="text-3xl font-bold mt-3 tracking-tight">
          {showBalance
            ? `${theme.currencySymbol}${Number(balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "******"}
        </p>

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => navigate("/purchase")}
            className="bg-black/40 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-black/60 transition"
          >
            + Add Money
          </button>
          <button className="bg-white/15 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/20">
            Pay Bills
          </button>
        </div>
      </motion.div>

      {/* Sales Info */}
      <div className={`mx-4 mt-3 p-3 rounded-xl ${theme.cardBg} flex items-center justify-between`}>
        <p className="text-xs text-gray-400">
          <span className={theme.accentColor}>●</span> Business Service · Today's Sales: {theme.currencySymbol}0.00
        </p>
        <span className="text-xs text-gray-600">›</span>
      </div>

      {/* Recent Transactions */}
      <div className={`mx-4 mt-4 ${theme.cardBg} rounded-2xl p-4 space-y-3 border border-white/5`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">Recent Transactions</p>
          <button className="text-xs text-gray-400">See All</button>
        </div>

        {transactions.length > 0 ? (
          transactions.slice(0, 3).map((tx, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <div>
                <p className="font-medium text-sm">{tx?.title || "Transaction"}</p>
                <p className="text-xs text-gray-400">
                  {tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : ""}
                </p>
              </div>

              <div className="text-right">
                <p
                  className={`font-semibold text-sm ${
                    tx?.status === "failed"
                      ? "text-red-400"
                      : tx?.status === "confirmed"
                      ? "text-green-400"
                      : "text-yellow-400"
                  }`}
                >
                  {theme.currencySymbol}{Number(tx?.amount ?? 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-400 capitalize">{tx?.status}</p>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 text-sm py-4">No recent transactions</p>
        )}
      </div>

      {/* Quick Actions — transfer buttons open the branded selector
          (this wallet's identity: "opay") */}
      <div className="mx-4 mt-5 grid grid-cols-3 gap-3">
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate("/transfer-select", { state: { from: "opay" } })}
          className={`${theme.actionBg} p-4 rounded-xl flex flex-col items-center`}
        >
          <div className="text-2xl">🏦</div>
          <p className="text-xs mt-2 font-medium">{theme.transferToSelf}</p>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate("/transfer-select", { state: { from: "opay" } })}
          className={`${theme.actionBg} p-4 rounded-xl flex flex-col items-center`}
        >
          <div className="text-2xl">🏛️</div>
          <p className="text-xs mt-2 font-medium">{theme.transferToBank}</p>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.96 }}
          className={`${theme.actionBg} p-4 rounded-xl flex flex-col items-center`}
        >
          <div className="text-2xl">💸</div>
          <p className="text-xs mt-2 font-medium">Withdraw</p>
        </motion.button>
      </div>

      {/* Services Grid */}
      <div className="mx-4 mt-6 grid grid-cols-4 gap-4">
        {theme.services.map((item, idx) => (
          <motion.button
            key={idx}
            whileTap={{ scale: 0.95 }}
            className="flex flex-col items-center"
          >
            <div className={`w-12 h-12 ${theme.cardBg} rounded-xl flex items-center justify-center text-lg border border-white/5`}>
              {item.emoji}
            </div>
            <p className="text-xs mt-1 text-gray-300">{item.label}</p>
          </motion.button>
        ))}
      </div>

      {/* Bottom Navigation */}
      <div className={`fixed bottom-0 w-full ${theme.bgColor} border-t border-gray-800 py-2 flex justify-around text-xs`}>
        <button className={theme.bottomNavActive}>Home</button>
        <button className="text-gray-400">Rewards</button>
        <button className="text-gray-400">Finance</button>
        <button className="text-gray-400">Cards</button>
        <button className="text-gray-400">Me</button>
      </div>
    </div>
  );
};

export default WalletPage;
